// Estado de "Quién va al evento" para la pestaña Logística 2 (formato nuevo).
//
// Mismos datos, endpoints, cálculos y cola de autoguardado que la tarjeta
// QuienVaEvento de la pestaña Logística:
// - GET  /presupuestos/{id}/quien-va-evento (defectos, guardado y tarifas)
// - PUT  /presupuestos/{id}/quien-va-evento (modo, tramos, personal, permanencia,
//   recogida; o solo `solapes` para decidir un solape)
// - PUT  /presupuestos/{id}/quien-va-evento/persona (persona de Duetto que va)
// - POST /presupuestos/{id}/suplemento-recogida (regalar la línea "Recogida")
// El cálculo al momento es el de utils/quienVaEvento.js (réplica del backend).
// Lo usa el contenedor de Logística 2 una sola vez y reparte el estado entre el
// resumen, el bloque Quién va y los costes internos.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/api';
import {
  calcularEstadoGrupo,
  firmaEstado,
  horasDeTandas,
  limpiarMenuEditado,
  limpiarPorJornada,
  limpiarTandas,
  minutosDeHora,
  personasFila,
  quienPorDefecto,
  recogidaDe,
  sumarCalculos,
  sumarComparativas,
} from '../utils/quienVaEvento';
import { esPersonaEventos, sinCosteEvento } from '../utils/empleadosEventos';

const useQuienVaEvento = ({
  presupuestoId, presupuesto, onGuardado, onEstadoGuardado, onPersonaGuardada, autoguardado = null,
  // Sube tras guardar una hora de servicio: se vuelve a pedir con lo ya guardado
  // (el cambio de la ficha llega antes que el guardado con retardo)
  recarga = 0,
}) => {
  const autoguardadoRef = useRef(autoguardado);
  autoguardadoRef.current = autoguardado;
  // Con autoguardado, persona, solapes y recogida van por la misma cola
  const encolar = (clave, fn) => (autoguardadoRef.current
    ? autoguardadoRef.current.programar(`${clave}:${presupuestoId}`, fn, { inmediato: true }).catch(() => {})
    : fn());
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [modo, setModoEstado] = useState(null);
  const [valores, setValores] = useState({});
  const [editados, setEditados] = useState([]);
  const [menuEditado, setMenuEditado] = useState({});
  const [permanencia, setPermanencia] = useState('se_queda');
  const [horaRecogida, setHoraRecogidaEstado] = useState('');
  const [tipoRecogida, setTipoRecogida] = useState('mismo_dia');
  const [fechaRecogida, setFechaRecogidaEstado] = useState('');
  const [quienRecoge, setQuienRecogeEstado] = useState('responsable');
  const [minutosRecogida, setMinutosRecogidaEstado] = useState(0);
  const [regalando, setRegalando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null); // { tipo: 'ok' | 'error', texto }
  const [empleados, setEmpleados] = useState(null);
  const [guardandoPersona, setGuardandoPersona] = useState(false);
  const [avisoPersona, setAvisoPersona] = useState(null);
  const [decidiendoSolape, setDecidiendoSolape] = useState(null);
  // Multievento: quién va propio de cada jornada {jornada_id: {modo?, personal_menu_editado?}}
  const [porJornada, setPorJornada] = useState({});

  // Lo que cambia los valores por defecto: si cambia, se recargan del backend
  const firmaDefectos = JSON.stringify([
    presupuesto?.hora_inicio,
    presupuesto?.hora_fin,
    presupuesto?.tiempo_viaje_min,
    presupuesto?.distancia_km,
    presupuesto?.num_personas,
    presupuesto?.es_multi_evento,
    presupuesto?.costes_version,
    (presupuesto?.menus_personalizados || []).map(m => [m.menu_id, m.nombre_menu, m.num_personas, m.personal_extra_real, m.config_personalizada,
      m.hora_inicio_servicio, m.hora_fin_servicio, m.requiere_horario]),
    // Las horas de servicio de cada jornada (hora propia del día) mueven sus tramos
    (presupuesto?.jornadas || []).map(j => [j.id, j.fecha, j.tiempo_viaje_min, j.distancia_km, j.hora_inicio, j.hora_fin,
      j.num_personas, (j.menus_personalizados || []).map(m => [m.menu_id, m.num_personas,
        m.hora_inicio_servicio, m.hora_fin_servicio])]),
    recarga,
  ]);

  const aplicarDatos = useCallback((data) => {
    setDatos(data);
    setModoEstado(data.modo || null);
    setValores(data.tramos || {});
    setEditados(data.tramos_editados || []);
    setMenuEditado(limpiarMenuEditado(data.guardado?.personal_menu_editado));
    setPermanencia(data.guardado?.permanencia === 'se_va' ? 'se_va' : 'se_queda');
    const rec = recogidaDe(data.guardado);
    setHoraRecogidaEstado(rec?.hora || data.guardado?.hora_recogida || '');
    setTipoRecogida(rec?.tipo || 'mismo_dia');
    setFechaRecogidaEstado(rec?.fecha || '');
    setQuienRecogeEstado(rec?.quien || 'responsable');
    setMinutosRecogidaEstado(rec?.minutos || 0);
    setPorJornada(limpiarPorJornada(data.guardado?.por_jornada));
  }, []);

  useEffect(() => {
    if (!presupuestoId) return undefined;
    let cancelado = false;
    setCargando(true);
    api.get(`/presupuestos/${presupuestoId}/quien-va-evento`)
      .then(({ data }) => {
        if (cancelado) return;
        if (!data || !data.tramos_defecto) {
          setDatos(null);
          setError('No se pudieron cargar los datos de quién va.');
          return;
        }
        aplicarDatos(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelado) return;
        console.error('Error cargando quién va al evento:', err);
        setError(err?.response?.data?.detail || 'No se pudieron cargar los datos de quién va.');
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [presupuestoId, firmaDefectos, aplicarDatos]);

  // Personas de Duetto para elegir quién va (sin cuentas técnicas). Se cargan
  // siempre (el resumen enseña el nombre y si es socio)
  useEffect(() => {
    if (empleados !== null) return undefined;
    let cancelado = false;
    api.get('/empleados')
      .then(({ data }) => {
        if (cancelado) return;
        const activos = (Array.isArray(data) ? data : []).filter(e => e && e.id && e.activo !== false && esPersonaEventos(e));
        activos.sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
        setEmpleados(activos);
      })
      .catch(() => { if (!cancelado) setEmpleados([]); });
    return () => { cancelado = true; };
  }, [empleados]);

  const guardarPersonaAhora = async (personaId) => {
    setGuardandoPersona(true);
    setAvisoPersona(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      const { data } = await api.put(`/presupuestos/${presupuestoId}/quien-va-evento/persona`, { persona_id: personaId || null });
      const q = data?.quien_va_evento || null;
      if (q) {
        setDatos(prev => (prev ? {
          ...prev,
          guardado: { ...(prev.guardado || {}), ...q },
          persona_sin_coste: !!data?.persona_sin_coste,
        } : prev));
      }
      if (onPersonaGuardada && data) onPersonaGuardada(data);
      if (onEstadoGuardado) onEstadoGuardado('ok');
    } catch (err) {
      setAvisoPersona(`No se pudo guardar: ${err?.response?.data?.detail || err.message}`);
      if (onEstadoGuardado) onEstadoGuardado('error');
    } finally {
      setGuardandoPersona(false);
    }
  };
  const guardarPersona = (personaId) => encolar('quien_va_persona', () => guardarPersonaAhora(personaId));

  // Multievento: persona de una jornada (`heredar`: vuelve a la del evento)
  const guardarPersonaJornadaAhora = async (jornadaId, personaId, heredar) => {
    setGuardandoPersona(true);
    setAvisoPersona(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      const { data } = await api.put(`/presupuestos/${presupuestoId}/quien-va-evento/persona`, {
        persona_id: heredar ? null : (personaId || null), jornada_id: jornadaId, heredar: !!heredar,
      });
      const q = data?.quien_va_evento || null;
      if (q) {
        setDatos(prev => (prev ? {
          ...prev,
          guardado: { ...(prev.guardado || {}), ...q },
          jornadas: (prev.jornadas || []).map(j => (j.id === jornadaId && !heredar
            ? { ...j, persona_sin_coste: !!data?.persona_sin_coste } : j)),
        } : prev));
      }
      if (onPersonaGuardada && data) onPersonaGuardada(data);
      if (onEstadoGuardado) onEstadoGuardado('ok');
    } catch (err) {
      setAvisoPersona(`No se pudo guardar: ${err?.response?.data?.detail || err.message}`);
      if (onEstadoGuardado) onEstadoGuardado('error');
    } finally {
      setGuardandoPersona(false);
    }
  };
  const guardarPersonaJornada = (jornadaId, personaId, heredar = false) => encolar(
    `quien_va_persona_${jornadaId}`, () => guardarPersonaJornadaAhora(jornadaId, personaId, heredar));

  const tarifas = useMemo(() => ({
    propio: datos?.tarifas?.propio ?? datos?.calculo?.tarifa_propio ?? 0,
    extra: datos?.tarifas?.extra ?? datos?.calculo?.tarifa_extra ?? 0,
  }), [datos]);

  const personaIdGuardada = datos?.guardado?.persona_id || '';
  const empleadoElegido = personaIdGuardada ? (empleados || []).find(e => e.id === personaIdGuardada) : null;
  let sinCostePersona = false;
  if (personaIdGuardada) sinCostePersona = empleadoElegido ? sinCosteEvento(empleadoElegido) : !!datos?.persona_sin_coste;

  const recogida = useMemo(() => (permanencia === 'se_va' ? {
    tipo: tipoRecogida,
    fecha: tipoRecogida === 'otro_dia' ? (fechaRecogida || null) : null,
    hora: horaRecogida || null,
    quien: quienRecoge || quienPorDefecto(tipoRecogida),
    minutos: quienRecoge === 'otro_evento' ? (Number(minutosRecogida) || 0) : 0,
  } : null), [permanencia, tipoRecogida, fechaRecogida, horaRecogida, quienRecoge, minutosRecogida]);

  // Mismo cálculo que el backend y el motor de costes, con lo que se está editando
  const { raiz, jornadas } = useMemo(() => {
    if (!datos) return { raiz: null, jornadas: [] };
    const comunes = {
      menuEditado, valores, editados, permanencia, horaRecogida: horaRecogida || null,
      tarifas, costeKm: datos.coste_km ?? 0.1, modo, recogida, sinCostePropio: sinCostePersona,
    };
    const grupoRaiz = {
      ...datos,
      hora_inicio: datos.hora_inicio ?? presupuesto?.hora_inicio,
      hora_fin: datos.hora_fin ?? presupuesto?.hora_fin,
      fecha: presupuesto?.fecha_evento || null,
    };
    // Multievento: cada jornada con su quién va (modo, persona y personal del
    // menú propios de la jornada o, si no los tiene, los del evento)
    const guardadoPorJornada = datos.guardado?.por_jornada || {};
    return {
      raiz: calcularEstadoGrupo({ grupo: grupoRaiz, ...comunes }),
      jornadas: (datos.jornadas || []).map((j) => {
        const pj = porJornada[j.id] || {};
        const guardadoJ = guardadoPorJornada[j.id] || {};
        const personaPropia = Object.prototype.hasOwnProperty.call(guardadoJ, 'persona_id');
        const personaIdJ = personaPropia ? (guardadoJ.persona_id || '') : personaIdGuardada;
        const empleadoJ = personaIdJ ? (empleados || []).find(e => e.id === personaIdJ) : null;
        let sinCosteJ = false;
        if (personaIdJ) {
          sinCosteJ = empleadoJ ? sinCosteEvento(empleadoJ) : !!(personaPropia ? j.persona_sin_coste : datos.persona_sin_coste);
        }
        const modoJ = pj.modo || modo;
        const menuJ = { ...menuEditado, ...(pj.personal_menu_editado || {}) };
        let personaNombreJ = datos.guardado?.persona_nombre || '';
        if (personaPropia) personaNombreJ = guardadoJ.persona_nombre || '';
        if (!personaNombreJ && empleadoJ) personaNombreJ = empleadoJ.nombre;
        return {
          j,
          modo: modoJ,
          personaId: personaIdJ,
          personaNombre: personaNombreJ,
          sinCoste: sinCosteJ,
          propio: {
            modo: !!pj.modo,
            persona: personaPropia,
            personal: Object.keys(pj.personal_menu_editado || {}).length > 0,
          },
          menuEditado: menuJ,
          est: calcularEstadoGrupo({
            grupo: { ...j, fecha: (presupuesto?.jornadas || []).find(x => x.id === j.id)?.fecha || null },
            ...comunes,
            modo: modoJ,
            menuEditado: menuJ,
            sinCostePropio: sinCosteJ,
          }),
        };
      }),
    };
  }, [datos, menuEditado, valores, editados, permanencia, horaRecogida, tarifas, modo, recogida, sinCostePersona,
    porJornada, empleados, personaIdGuardada,
    presupuesto?.hora_inicio, presupuesto?.hora_fin, presupuesto?.fecha_evento, presupuesto?.jornadas]);

  const multi = jornadas.length > 0;
  const tramos = raiz ? raiz.tramos : {};
  const calculo = raiz
    ? (multi ? sumarCalculos(jornadas.map(x => x.est.calculo), modo, jornadas.map(x => x.modo)) : raiz.calculo)
    : null;
  const comparativa = raiz ? (multi ? sumarComparativas(jornadas.map(x => x.est.comparativa)) : raiz.comparativa) : null;
  const modoGuardado = datos?.modo || null;
  const permanenciaGuardada = datos?.guardado?.permanencia === 'se_va' ? 'se_va' : 'se_queda';
  // Quién va por jornada: cuenta como cambio igual que lo del evento
  const firmaPorJornada = (pj) => (multi ? JSON.stringify(limpiarPorJornada(pj)) : '');
  const hayCambios = !!datos && (firmaEstado(modo, editados, tramos, menuEditado, permanencia, recogida)
    !== firmaEstado(datos.modo, datos.tramos_editados, datos.tramos, limpiarMenuEditado(datos.guardado?.personal_menu_editado),
      permanenciaGuardada, recogidaDe(datos.guardado))
    || firmaPorJornada(porJornada) !== firmaPorJornada(datos.guardado?.por_jornada));

  // Autoguardado: con cambios (y lo mínimo para guardar) se programa el guardado
  // en la cola de la ficha; si se vuelve a lo guardado, se cancela
  const hayDatos = !!datos;
  const firmaActual = hayDatos
    ? `${firmaEstado(modo, editados, tramos, menuEditado, permanencia, recogida)}|${firmaPorJornada(porJornada)}`
    : null;
  const firmaActualRef = useRef(firmaActual);
  firmaActualRef.current = firmaActual;
  const firmaEnviadaRef = useRef(null);
  let faltaParaGuardar = null;
  if (!modo) faltaParaGuardar = 'Elige quién va para que se guarde.';
  else if (permanencia === 'se_va' && tipoRecogida === 'otro_dia' && (!fechaRecogida || !horaRecogida)) {
    faltaParaGuardar = 'Indica la fecha y la hora de la recogida para que se guarde.';
  }

  // Personal externo según lo guardado (lo que usa la tarea de Por hacer); en
  // multievento, el de la jornada que más lleva
  const personasGuardadas = (filas) => (filas || []).reduce((s, f) => s + personasFila(f), 0);
  const externosGuardados = datos ? ((multi
    ? Math.max(0, ...(datos.jornadas || []).map(j => personasGuardadas(j.personal_menu)))
    : personasGuardadas(datos?.personal_menu))
    + (modoGuardado === 'extra' ? 1 : 0)) : 0;

  // Personal externo del cálculo en curso, con el desglose camareros / extras.
  // Multievento: la jornada que más lleva.
  const externosCalculo = (() => {
    const grupos = multi ? jornadas.map(x => x.est) : (raiz ? [raiz] : []);
    let mejor = { total: 0, camareros: 0, extras: 0 };
    grupos.forEach((est) => {
      const total = Number(est?.calculo?.personasExternas) || 0;
      const camareros = (est?.filas || []).reduce(
        (t, f) => t + (f?.cubre_responsable ? 0 : Math.max(0, parseInt(f?.camareros, 10) || 0)), 0);
      if (total > mejor.total) mejor = { total, camareros: Math.min(camareros, total), extras: Math.max(0, total - camareros) };
    });
    return mejor;
  })();

  const cambiarTramo = (clave, texto) => {
    const limpio = String(texto).replace(/[^\d]/g, '');
    setValores(prev => ({ ...prev, [clave]: limpio === '' ? 0 : Math.min(1440, parseInt(limpio, 10)) }));
    setEditados(prev => (prev.includes(clave) ? prev : [...prev, clave]));
    setAviso(null);
  };

  const restablecerTramo = (clave) => {
    setEditados(prev => prev.filter(t => t !== clave));
    setAviso(null);
  };

  const valorMenu = (campo, texto) => {
    if (campo === 'horas') {
      const n = parseFloat(String(texto).replace(',', '.'));
      return Number.isNaN(n) ? 0 : Math.min(24, Math.max(0, Math.round(n * 100) / 100));
    }
    const limpio = String(texto).replace(/[^\d]/g, '');
    return limpio === '' ? 0 : Math.min(500, parseInt(limpio, 10));
  };

  const cambiarMenu = (clave, campo, texto) => {
    const valor = valorMenu(campo, texto);
    setMenuEditado(prev => ({ ...prev, [clave]: { ...(prev[clave] || {}), [campo]: valor } }));
    setAviso(null);
  };

  // ----- Multievento: quién va propio de una jornada -----
  const ajustarJornada = (jornadaId, fn) => {
    setPorJornada((prev) => {
      const nuevo = fn({ ...(prev[jornadaId] || {}) });
      const res = { ...prev };
      if (nuevo && (nuevo.modo || Object.keys(nuevo.personal_menu_editado || {}).length > 0)) res[jornadaId] = nuevo;
      else delete res[jornadaId];
      return res;
    });
    setAviso(null);
  };
  // Modo del responsable en la jornada (null: el del evento)
  const setModoJornada = (jornadaId, m) => ajustarJornada(jornadaId, (pj) => {
    const res = { ...pj };
    if (m === 'propio' || m === 'extra') res.modo = m;
    else delete res.modo;
    return res;
  });
  // Personal del menú de la jornada: parte de lo del evento para esa línea
  const cambiarMenuFilaJornada = (jornadaId, clave, campos) => ajustarJornada(jornadaId, (pj) => {
    const pme = { ...(pj.personal_menu_editado || {}) };
    pme[clave] = { ...(menuEditado[clave] || {}), ...(pme[clave] || {}), ...campos };
    return { ...pj, personal_menu_editado: pme };
  });
  const cambiarMenuJornada = (jornadaId, clave, campo, texto) => cambiarMenuFilaJornada(
    jornadaId, clave, { [campo]: valorMenu(campo, texto) });
  const cambiarCubreJornada = (jornadaId, clave, valor) => cambiarMenuFilaJornada(
    jornadaId, clave, { cubre_responsable: valor });
  // Quita lo propio de la jornada en esa línea (vuelve a lo del evento)
  const restablecerMenuJornada = (jornadaId, clave) => ajustarJornada(jornadaId, (pj) => {
    const pme = { ...(pj.personal_menu_editado || {}) };
    delete pme[clave];
    return { ...pj, personal_menu_editado: pme };
  });

  // ----- Tandas de camareros (unos a una hora y otros a otra) -----
  // Campos de una fila con las tandas nuevas: camareros = suma y horas = media
  // ponderada (como el servidor). Sin tandas (null o []), vuelve al modo simple
  // con los camareros y las horas que salían de ellas.
  const conTandas = (campos, tandas) => {
    const res = { ...(campos || {}) };
    const limpias = limpiarTandas(tandas);
    if (limpias.length > 0) {
      res.tandas = limpias;
      res.camareros = limpias.reduce((s, t) => s + t.camareros, 0);
      res.horas = horasDeTandas(limpias);
    } else {
      delete res.tandas;
    }
    return res;
  };
  const cambiarTandas = (clave, tandas) => {
    setMenuEditado(prev => ({ ...prev, [clave]: conTandas(prev[clave], tandas) }));
    setAviso(null);
  };
  // Multievento: tandas de la línea en esa jornada (parte de lo del evento)
  const cambiarTandasJornada = (jornadaId, clave, tandas) => ajustarJornada(jornadaId, (pj) => {
    const pme = { ...(pj.personal_menu_editado || {}) };
    pme[clave] = conTandas({ ...(menuEditado[clave] || {}), ...(pme[clave] || {}) }, tandas);
    return { ...pj, personal_menu_editado: pme };
  });

  // "Volver a lo propuesto": camareros, extras, horas y tandas como los calcula
  // el ERP; la casilla "Lo cubre la persona que va" se conserva
  const soloCubre = (campos) => (typeof campos?.cubre_responsable === 'boolean'
    ? { cubre_responsable: campos.cubre_responsable } : null);
  const volverAPropuesto = (clave) => {
    setMenuEditado((prev) => {
      const res = { ...prev };
      const cubre = soloCubre(prev[clave]);
      if (cubre) res[clave] = cubre;
      else delete res[clave];
      return res;
    });
    setAviso(null);
  };
  // En una jornada: si el evento tiene esa línea editada, la jornada lleva los
  // valores propuestos de forma explícita (si no, heredaría lo del evento)
  const volverAPropuestoJornada = (jornadaId, clave, defecto = {}) => ajustarJornada(jornadaId, (pj) => {
    const pme = { ...(pj.personal_menu_editado || {}) };
    const actual = { ...(menuEditado[clave] || {}), ...(pme[clave] || {}) };
    const eventoEditado = Object.keys(menuEditado[clave] || {}).some(c => c !== 'cubre_responsable');
    const cubre = soloCubre(actual);
    if (eventoEditado) {
      pme[clave] = {
        camareros: parseInt(defecto.camareros, 10) || 0,
        extras: parseInt(defecto.extras, 10) || 0,
        horas: Number(defecto.horas) || 0,
        ...(cubre || {}),
      };
    } else if (cubre) {
      pme[clave] = cubre;
    } else {
      delete pme[clave];
    }
    return { ...pj, personal_menu_editado: pme };
  });

  // "Lo hace <persona que va>": esa fila no suma personas ni coste
  const cambiarCubre = (clave, valor) => {
    setMenuEditado(prev => ({ ...prev, [clave]: { ...(prev[clave] || {}), cubre_responsable: valor } }));
    setAviso(null);
  };

  const restablecerMenu = (clave) => {
    setMenuEditado((prev) => {
      const res = { ...prev };
      delete res[clave];
      return res;
    });
    setAviso(null);
  };

  const decidirSolapeAhora = async (clave, decision) => {
    setDecidiendoSolape(clave);
    setAviso(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      const { data } = await api.put(`/presupuestos/${presupuestoId}/quien-va-evento`, { solapes: { [clave]: decision } });
      if (data && data.tramos_defecto) {
        if (hayCambios) setDatos(data);
        else aplicarDatos(data);
      }
      if (onGuardado) onGuardado(data?.guardado || null, data);
      if (onEstadoGuardado) onEstadoGuardado('ok');
    } catch (err) {
      setAviso({ tipo: 'error', texto: `No se pudo guardar la decisión: ${err?.response?.data?.detail || err.message}` });
      if (onEstadoGuardado) onEstadoGuardado('error');
    } finally {
      setDecidiendoSolape(null);
    }
  };
  // Solape de montaje: "extra" | "ignorado" | null (deshacer, vuelve el aviso)
  const decidirSolape = (clave, decision) => {
    if (!clave || decidiendoSolape) return undefined;
    return encolar(`quien_va_solape_${clave}`, () => decidirSolapeAhora(clave, decision));
  };

  const setModo = (m) => { setModoEstado(m); setAviso(null); };
  const cambiarPermanencia = (seVa) => { setPermanencia(seVa ? 'se_va' : 'se_queda'); setAviso(null); };
  const cambiarTipoRecogida = (tipo) => {
    setTipoRecogida(tipo);
    setQuienRecogeEstado(quienPorDefecto(tipo));
    setAviso(null);
  };
  const setHoraRecogida = (v) => { setHoraRecogidaEstado(v); setAviso(null); };
  const setFechaRecogida = (v) => { setFechaRecogidaEstado(v); setAviso(null); };
  const setQuienRecoge = (v) => { setQuienRecogeEstado(v); setAviso(null); };
  const setMinutosRecogida = (v) => { setMinutosRecogidaEstado(v); setAviso(null); };

  // Hora de llegada a la nave o de fin de la descarga escrita a mano: se ajusta
  // el tramo de al lado (carga o descarga) y queda como editado
  const ajustarExtremo = (tramo, horaNueva, horario = null) => {
    // `horario`: el de una jornada (multievento); si no, el del evento
    const bloques = (horario || raiz?.horario)?.bloques || [];
    const nueva = minutosDeHora(horaNueva);
    if (nueva === null || bloques.length === 0) return;
    if (tramo === 'carga') {
      const carga = bloques[0].tramos.find(t => t.clave === 'carga');
      if (!carga) return;
      const fin = minutosDeHora(carga.fin);
      cambiarTramo('carga', String((((fin - nueva) % 1440) + 1440) % 1440));
    } else {
      const ultimo = bloques[bloques.length - 1];
      const descarga = ultimo.tramos.find(t => t.clave === 'descarga');
      if (!descarga) return;
      const ini = minutosDeHora(descarga.inicio);
      cambiarTramo('descarga', String((((nueva - ini) % 1440) + 1440) % 1440));
    }
  };

  const guardar = async ({ automatico = false } = {}) => {
    if (!modo) {
      setAviso({ tipo: 'error', texto: 'Elige quién va: alguien de Duetto o un extra externo.' });
      return undefined;
    }
    const firmaInicio = firmaActualRef.current;
    setGuardando(true);
    setAviso(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      const personalMenuEditado = limpiarMenuEditado(menuEditado);
      const cuerpo = {
        modo,
        tramos,
        tramos_editados: editados,
        personal_menu_editado: personalMenuEditado,
        permanencia,
        hora_recogida: permanencia === 'se_va' && horaRecogida ? horaRecogida : null,
        recogida: permanencia === 'se_va' ? {
          tipo: tipoRecogida,
          fecha: tipoRecogida === 'otro_dia' ? (fechaRecogida || null) : null,
          hora: horaRecogida || null,
          quien: quienRecoge,
          minutos: quienRecoge === 'otro_evento' ? (Number(minutosRecogida) || 0) : 0,
        } : null,
        // Multievento: lo propio de cada jornada (modo y personal del menú)
        ...(multi ? { por_jornada: limpiarPorJornada(porJornada) } : {}),
      };
      if (permanencia === 'se_va' && tipoRecogida === 'otro_dia' && (!fechaRecogida || !horaRecogida)) {
        setAviso({ tipo: 'error', texto: 'Indica la fecha y la hora de la recogida.' });
        setGuardando(false);
        if (onEstadoGuardado) onEstadoGuardado('error');
        return undefined;
      }
      firmaEnviadaRef.current = firmaInicio;
      const { data } = await api.put(`/presupuestos/${presupuestoId}/quien-va-evento`, cuerpo);
      const siguenEditando = firmaActualRef.current !== firmaInicio;
      const guardadoLocal = {
        ...cuerpo,
        personal_menu: (raiz?.filas || []).map(f => ({
          clave: f.clave, nombre: f.nombre, personas: f.personas, camareros: f.camareros, extras: f.extras, horas: f.horas,
          cubre_responsable: !!f.cubre_responsable,
          ...(Array.isArray(f.tandas) && f.tandas.length > 0 ? { tandas: f.tandas } : {}),
        })),
        total_minutos: calculo?.totalMinutos,
        tarifa_propio: tarifas.propio,
        tarifa_extra: tarifas.extra,
        coste_responsable: modo === 'extra' ? calculo?.costeExtra : calculo?.costePropio,
        coste_personal_menu: calculo?.personalMenu?.coste,
        coste_total: calculo?.total,
      };
      if (data && data.tramos_defecto) {
        if (siguenEditando) setDatos(data);
        else aplicarDatos(data);
      } else {
        setDatos(prev => (prev ? { ...prev, modo, tramos, tramos_editados: editados, guardado: guardadoLocal } : prev));
      }
      if (onGuardado) onGuardado(data?.guardado || guardadoLocal, data);
      if (!automatico) setAviso({ tipo: 'ok', texto: 'Guardado.' });
      if (onEstadoGuardado) onEstadoGuardado('ok');
      return data;
    } catch (err) {
      firmaEnviadaRef.current = null;
      setAviso({ tipo: 'error', texto: `No se pudo guardar: ${err?.response?.data?.detail || err.message}` });
      if (onEstadoGuardado) onEstadoGuardado('error');
      if (automatico) throw err;
      return undefined;
    } finally {
      setGuardando(false);
    }
  };
  const guardarRef = useRef(guardar);
  guardarRef.current = guardar;

  useEffect(() => {
    const a = autoguardadoRef.current;
    if (!a || !hayDatos) return;
    const clave = `quien_va:${presupuestoId}`;
    if (!hayCambios || faltaParaGuardar) {
      a.cancelar(clave);
      return;
    }
    if (firmaEnviadaRef.current !== null && firmaEnviadaRef.current === firmaActual) return;
    a.programar(clave, () => guardarRef.current({ automatico: true })).catch(() => {});
  }, [firmaActual, hayCambios, faltaParaGuardar, hayDatos, presupuestoId]);

  // Línea de venta "Recogida" del presupuesto (se añade sola al guardar)
  const lineaRecogida = (presupuesto?.suplementos || []).find(x => x && x.es_suplemento_recogida) || null;
  const regalarRecogidaAhora = async (accion) => {
    setRegalando(true);
    setAviso(null);
    try {
      const { data } = await api.post(`/presupuestos/${presupuestoId}/suplemento-recogida`, { accion });
      if (onGuardado && data) {
        onGuardado(datos?.guardado, {
          suplementos: data.suplementos, precio_total: data.precio_total, importe_base: data.importe_base,
          iva: data.iva, importe_total: data.importe_total,
        });
      }
      setAviso({ tipo: 'ok', texto: accion === 'regalar' ? 'Recogida regalada.' : 'La recogida vuelve a cobrarse.' });
    } catch (err) {
      setAviso({ tipo: 'error', texto: `No se pudo cambiar la recogida: ${err?.response?.data?.detail || err.message}` });
    } finally {
      setRegalando(false);
    }
  };
  const regalarRecogida = (accion) => encolar('quien_va_recogida', () => regalarRecogidaAhora(accion));

  const personaId = datos?.guardado?.persona_id || '';
  const personaNombre = datos?.guardado?.persona_nombre
    || (empleadoElegido ? empleadoElegido.nombre : '');

  return {
    datos, cargando, error, modo, setModo, valores, editados, menuEditado, permanencia, seVa: permanencia === 'se_va',
    horaRecogida, tipoRecogida, fechaRecogida, quienRecoge, minutosRecogida, recogida,
    setHoraRecogida, setFechaRecogida, setQuienRecoge, setMinutosRecogida, cambiarPermanencia, cambiarTipoRecogida,
    regalando, guardando, aviso, empleados, guardandoPersona, avisoPersona, decidiendoSolape,
    tarifas, sinCostePersona, raiz, jornadas, multi, tramos, calculo, comparativa, modoGuardado,
    hayCambios, faltaParaGuardar, externosGuardados, externosCalculo, personaId, personaNombre, lineaRecogida,
    guardarPersona, cambiarTramo, restablecerTramo, cambiarMenu, cambiarCubre, restablecerMenu,
    cambiarTandas, volverAPropuesto,
    decidirSolape, ajustarExtremo, guardar, regalarRecogida,
    // Multievento: quién va de cada jornada
    porJornada, setModoJornada, cambiarMenuJornada, cambiarCubreJornada, restablecerMenuJornada, guardarPersonaJornada,
    cambiarTandasJornada, volverAPropuestoJornada,
  };
};

export default useQuienVaEvento;
